import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { fetchRestaurants } from '../lib/api';
import { Stars, Prix, StatutBadge, Spinner, Button } from '../components/ui/UI';
import RestaurantDetail from '../components/restaurant/RestaurantDetail';
import 'leaflet/dist/leaflet.css';

// Fix icône Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function createIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);box-shadow:0 4px 12px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;border:2px solid white;">
      <span style="transform:rotate(45deg);font-size:14px;">🍽️</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
}

const iconTeste = createIcon('#10B981');
const iconTester = createIcon('#F59E0B');

function FlyToLocation({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 14, { duration: 1.5 }); }, [center, map]);
  return null;
}

export default function CartePage({ onAddClick }) {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [radius, setRadius] = useState(0);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterCuisine, setFilterCuisine] = useState('tous');
  const [filterPrix, setFilterPrix] = useState('tous');
  const [locating, setLocating] = useState(false);

  const CUISINES = ['tous', 'Italien', 'Japonais', 'Français', 'Mexicain', 'Indien', 'Chinois', 'Américain', 'Autre'];

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchRestaurants();
      setRestaurants(data.filter(r => r.lat && r.lng));
    } finally {
      setLoading(false);
    }
  }

  function locateMe() {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setUserPos([pos.coords.latitude, pos.coords.longitude]); setLocating(false); },
      () => { alert('Géolocalisation refusée ou indisponible.'); setLocating(false); }
    );
  }

  function distanceTo(r) {
    if (!userPos || !r.lat) return Infinity;
    const R = 6371;
    const dLat = (r.lat - userPos[0]) * Math.PI / 180;
    const dLng = (r.lng - userPos[1]) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(userPos[0]*Math.PI/180) * Math.cos(r.lat*Math.PI/180) * Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  const filtered = restaurants.filter(r => {
    if (filterStatut !== 'tous' && r.statut !== filterStatut) return false;
    if (filterCuisine !== 'tous' && r.type_cuisine !== filterCuisine) return false;
    if (filterPrix !== 'tous' && String(r.niveau_prix) !== filterPrix) return false;
    if (radius > 0 && userPos && distanceTo(r) > radius) return false;
    return true;
  });

  const defaultCenter = [48.8566, 2.3522]; // Paris
  const mapCenter = userPos || defaultCenter;

  return (
    <div style={{ height: 'calc(100vh - 62px)', display: 'flex', flexDirection: 'column' }}>

      {/* Filters bar */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '12px 20px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', zIndex: 10 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-2)' }}>🗺️ Carte</span>
        <span style={{ color: 'var(--border-2)' }}>|</span>

        {/* Statut */}
        {[{ v: 'tous', l: 'Tous' }, { v: 'teste', l: '✅ Testés' }, { v: 'tester', l: '📌 À tester' }].map(s => (
          <button key={s.v} onClick={() => setFilterStatut(s.v)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: filterStatut === s.v ? 'var(--brand)' : 'var(--surface-3)',
            color: filterStatut === s.v ? '#fff' : 'var(--text-2)', transition: 'all 0.15s',
          }}>{s.l}</button>
        ))}

        <span style={{ color: 'var(--border-2)' }}>|</span>

        {/* Prix */}
        {[{ v: 'tous', l: 'Prix' }, { v: '1', l: '€' }, { v: '2', l: '€€' }, { v: '3', l: '€€€' }].map(p => (
          <button key={p.v} onClick={() => setFilterPrix(p.v)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
            background: filterPrix === p.v ? '#059669' : 'var(--surface-3)',
            color: filterPrix === p.v ? '#fff' : 'var(--text-2)', transition: 'all 0.15s',
          }}>{p.l}</button>
        ))}

        <span style={{ color: 'var(--border-2)' }}>|</span>

        {/* Ma position */}
        <button onClick={locateMe} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: userPos ? 'var(--brand-pale)' : 'var(--surface)', color: userPos ? 'var(--brand)' : 'var(--text-2)', cursor: 'pointer' }}>
          {locating ? <Spinner size={12} /> : '📍'} {userPos ? 'Localisé' : 'Ma position'}
        </button>

        {userPos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Rayon :</span>
            {[0, 0.5, 1, 2, 5].map(km => (
              <button key={km} onClick={() => setRadius(km)} style={{
                padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: radius === km ? 'var(--brand)' : 'var(--surface-3)',
                color: radius === km ? '#fff' : 'var(--text-2)',
              }}>{km === 0 ? 'Tous' : `${km}km`}</button>
            ))}
          </div>
        )}

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>{filtered.length} restaurant{filtered.length > 1 ? 's' : ''}</span>

        <button onClick={onAddClick} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--brand)', color: '#fff', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          + Ajouter
        </button>
      </div>

      {/* Map */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={40} /></div>
      ) : (
        <div style={{ flex: 1 }}>
          <MapContainer center={mapCenter} zoom={12} style={{ width: '100%', height: '100%' }} zoomControl={true}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='© OpenStreetMap'
            />
            {userPos && <FlyToLocation center={userPos} />}
            {userPos && radius > 0 && (
              <Circle center={userPos} radius={radius * 1000} color="var(--brand)" fillColor="var(--brand)" fillOpacity={0.05} />
            )}
            {userPos && (
              <Marker position={userPos} icon={L.divIcon({ className: '', html: '<div style="width:16px;height:16px;background:var(--blue);border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(59,130,246,0.5)"></div>', iconSize: [16, 16], iconAnchor: [8, 8] })}>
                <Popup><strong>Vous êtes ici</strong></Popup>
              </Marker>
            )}
            {filtered.map(r => (
              <Marker key={r.id} position={[r.lat, r.lng]} icon={r.statut === 'teste' ? iconTeste : iconTester}>
                <Popup maxWidth={280}>
                  <div style={{ padding: '4px' }} onClick={() => setSelectedId(r.id)}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, marginBottom: 4, cursor: 'pointer', color: 'var(--brand)' }}>{r.nom}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>📍 {r.adresse}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatutBadge statut={r.statut} />
                      <Prix niveau={r.niveau_prix} />
                      {r.note_moyenne && <Stars note={Math.round(r.note_moyenne)} size={12} />}
                    </div>
                    <button onClick={() => setSelectedId(r.id)} style={{ marginTop: 10, width: '100%', padding: '6px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Voir les détails →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      <RestaurantDetail
        restaurantId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onUpdate={load}
      />
    </div>
  );
}
